import { useCallback, useEffect, useState } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { performPostRequest } from '@atlassian/jira-fetch';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { useProjectId, useProjectKey } from '@atlassian/jira-project-context-service';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import type { DefaultValue, FetchTransitionsPromise, UseDefaultStatusService } from './types';

const getTransitions = async (cloudId: string, projectId: string, issueTypeId: string) => {
	const resp = await performPostRequest(
		`/gateway/api/jira/project-configuration/query/${cloudId}/2/transition/initial`,
		{
			body: JSON.stringify({
				projectId,
				issueTypeId: issueTypeId.toString(),
			}),
		},
	);
	return resp;
};

export const useIssueType = () => useState<string | undefined>();

export const useDefaultStatus: UseDefaultStatusService = () => {
	const { cloudId } = useTenantContext();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey);
	const [defaultStatus, setDefaultStatus] = useState<DefaultValue | undefined>();
	const [issueType, setIssueType] = useIssueType();
	const fetchTransitionsService: FetchTransitionsPromise = useCallback(
		(issueTypeId: string) => getTransitions(cloudId, projectId ?? '', issueTypeId),
		[cloudId, projectId],
	);
	const { loading, error, data, fetch: fetchTransitions } = useService(fetchTransitionsService);

	const fetchDefaultStatus = useCallback(
		(issueTypeId: string) => {
			if (!loading && issueTypeId !== issueType) {
				fetchTransitions(issueTypeId);
				setIssueType(issueTypeId);
			}
		},
		[loading, issueType, fetchTransitions, setIssueType],
	);

	useEffect(() => {
		if (data && Array.isArray(data)) {
			const initialTransition = data.find((transition) => transition.isInitial === true);
			// Set default transition, for given issue type, if there is none, it's none
			setDefaultStatus(initialTransition?.to ?? undefined);
		}
	}, [data]);

	return [{ loading, error, data: defaultStatus }, fetchDefaultStatus];
};
